import React, { useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import PrescriptionInfoTab from '@/components/molecules/PrescriptionInfoTab'
import {
  GuidanceCheckPointList,
  KasanStatusPanel,
  useGuidanceQueryV3,
} from '@/features/guidance/v3'
import { PrescriptionData } from '@/shared/types/yakurekiPrescription'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'

interface VideoCallGuidancePanelProps {
  yakurekiId: string
  currentPrescription?: PrescriptionData
  previousPrescription?: PrescriptionData
}

// section 見出し. Notion spec (= docs/design-system/03 §3.2): 12px / weight 500 /
// fg.muted / 装飾なし. 枠線で囲わず余白と見出しで領域分割する (= §1 運用規則).
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        display: 'block',
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
)

// ビデオ通話中の指導支援パネル. 録音後の GuidancePrepPaneV3 と同じ V3 系統
// (確認要点 = checkPoints / 加算 = KasanStatusPanel) を表示する. 処方は別タブ。
export const VideoCallGuidancePanel: React.FC<VideoCallGuidancePanelProps> = ({
  yakurekiId,
  currentPrescription,
  previousPrescription,
}) => {
  const [tabValue, setTabValue] = useState(0)
  const guidance = useGuidanceQueryV3(yakurekiId)

  const checkPointsBlock = (() => {
    if (guidance.status === 'processing' || guidance.status === null) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          生成中…
        </Typography>
      )
    }
    if (guidance.status === 'failed') {
      return (
        <Typography variant="body2" color="error.main" sx={{ py: 0.5 }}>
          生成に失敗しました
        </Typography>
      )
    }
    if (guidance.status === 'skipped') {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
          NSIPS 未紐付けで生成不可
        </Typography>
      )
    }
    if (guidance.status === 'mismatch') {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
          この結果は表示できません (= 過去 V2 で生成されており、 現在の設定 (V3)
          と異なります)
        </Typography>
      )
    }
    if (guidance.status === 'completed' && guidance.data) {
      return <GuidanceCheckPointList points={guidance.data.checkPoints} />
    }
    return null
  })()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{
          minHeight: 36,
          borderBottom: `1px solid ${colorTokens.border.default}`,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
          },
        }}
      >
        <Tab label="指導支援" />
        <Tab label="処方情報" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {tabValue === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Section title="確認要点">{checkPointsBlock}</Section>
            <Section title="加算">
              <KasanStatusPanel yakurekiId={yakurekiId} />
            </Section>
          </Box>
        )}

        {tabValue === 1 && (
          <PrescriptionInfoTab
            currentPrescription={currentPrescription}
            previousPrescription={previousPrescription}
          />
        )}
      </Box>
    </Box>
  )
}
